import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { Link } from 'react-router-dom';
import {
  where, orderBy, limit,
} from 'firebase/firestore';
import { FileText, Clock, CheckCircle, XCircle, ClipboardList, Users, Wallet, Heart } from 'lucide-react';
import { PieChart, Pie, Cell } from 'recharts';
import { useBrandingContext } from '../contexts/BrandingContext';
import { useFirestoreQuery } from '../hooks/useFirestoreQuery';
import { useAssignments } from '../hooks/useAssignments';
import { AssignmentsLoadProblem, LoadProblem, RefreshProblem } from '../components/parent/LoadProblem';
import { queryKeys } from '../hooks/queryKeys';
import type { Declaration, FamilyFee, Language } from '@shared/types';
import { useDeclarations } from '../hooks/useDeclarations';
import { liveDeclarationTitle } from '@shared/utils/signedDocumentSource';
import { dateLocaleFor } from '@shared/utils/declarationVersionText';
import PageHeader from '../components/ui/PageHeader';
import StatCard from '../components/ui/StatCard';
import ChartCard from '../components/ui/ChartCard';
import Badge from '../components/ui/Badge';
import { CalendarWidget, CalendarLegend, LoadingSpinner, StatusPanel } from '../components/ui';
import type { CalendarEvent } from '../components/ui';
import { generateDonutPalette } from '../utils/brandColors';
import { useChildren } from '../hooks/useChildren';
import { useActiveSchoolYear } from '../hooks/useActiveSchoolYear';
import { useHolidayAdmin } from '../hooks/useHolidayAdmin';
import { useChildrenCalendarSlots } from '../hooks/useChildrenCalendarSlots';
import { buildParentCalendarEvents } from '../utils/parentCalendar';
import { childMarkColors } from '../utils/calendarPalette';
import { toIsoDate } from '../utils/teacherCalendar';
import type { TeacherHolidayView } from '@shared/types';
import { installmentStatus } from '@shared/utils/installmentStatus';

// ──────────────────────────────────────────────────────────────────────────────

export default function DashboardPage() {
  const { t, i18n } = useTranslation();
  const lang = ((i18n.language || 'bg').split('-')[0]) as Language;

  /**
   * Заглавието идва от ЖИВАТА декларация (решение Б2 на задача 24) — снимката в
   * възлагането не се обновява при преименуване. Същият резолвер, който ползват
   * `AssignmentsPage` и подписаният документ: една верига резерви, не четвърта.
   */
  const { declarations } = useDeclarations();
  const declarationByKey = useMemo(() => {
    const map = new Map<string, Declaration>();
    for (const declaration of declarations) {
      if (declaration.declarationId) map.set(declaration.declarationId, declaration);
      if (declaration.code) map.set(declaration.code, declaration);
    }
    return map;
  }, [declarations]);
  const { userProfile, loading: authLoading } = useAuth();
  const { primaryColor, secondaryColor } = useBrandingContext();

  const schoolId = userProfile?.schoolId;
  const familyId = userProfile?.familyId;
  /**
   * TASK_44 §1.2 — „не знам" ≠ „няма". Без familyId/schoolId заявките са ИЗКЛЮЧЕНИ: React Query
   * тогава дава `data: []` и `isLoading: false`, тоест старият екран показваше „Общо 0 · Няма
   * декларации" за родител, чиито декларации просто не са били потърсени. Сега това е отделно,
   * назовано състояние.
   */
  const notLinked = !authLoading && (!schoolId || !familyId);

  // ── data queries via React Query ─────────────────────────────────────────
  // 🔴 Същата кука като „Декларации“: ключът `byFamily` е общ, а React Query пази ЕДИН резултат на
  // ключ. До 19.10 таблото пишеше в него своя заявка (с `limit(50)`, без проверката за празен кеш)
  // — каквото покажеше „Декларации“, зависеше от това коя страница е отворена първа.
  const {
    assignments,
    isLoading: loadingAssignments,
    isSuccess: assignmentsOk,
    error: assignmentsError,
    refreshFailed: assignmentsRefreshFailed,
    refetch: refetchAssignments,
  } = useAssignments({ schoolId, familyId });
  // Паднало ОБНОВЯВАНЕ: числата и списъкът от сървъра остават, с бележка (ревю кр.2).
  const assignmentsLoaded = assignmentsOk || assignmentsRefreshFailed;
  const assignmentsLoadError = assignmentsRefreshFailed ? null : assignmentsError;

  const {
    data: familyFees = [],
    isLoading: loadingFees,
    isSuccess: feesLoaded,
    error: feesError,
  } = useFirestoreQuery<FamilyFee>({
    queryKey: queryKeys.school.familyFees.byFamily(schoolId || '', familyId || ''),
    collectionPath: `schools/${schoolId}/familyFees`,
    constraints: [
      where('familyId', '==', familyId),
      orderBy('schoolYear', 'desc'),
      limit(1),
    ],
    idField: 'familyFeeId',
    enabled: !!schoolId && !!familyId,
  });

  const familyFee = familyFees[0] || null;
  const loading = authLoading || (!notLinked && (loadingAssignments || loadingFees));

  // ── color palettes (brand-derived, same algorithm as admin/teacher) ────────
  const donutPalette = useMemo(
    () => generateDonutPalette(primaryColor || '#1e40af', secondaryColor || '#3b82f6'),
    [primaryColor, secondaryColor],
  );

  // ── часовете на децата и общите ваканции (задача 19) ──────────────────────
  //
  // Решение на owner (16.09.2026): общите ваканции и празници + часовете на
  // ВСЯКО дете, цялото семейство с един поглед. Без превключвател „избери дете".
  // Децата — за календара: паднали → казано (иначе часовете на децата изчезват мълчаливо).
  const { children, error: childrenError, refreshFailed: childrenRefreshFailed, refetch: refetchChildren } = useChildren();
  const childrenLoadError = childrenRefreshFailed ? null : childrenError;
  const refreshError = (assignmentsRefreshFailed ? assignmentsError : null) ?? (childrenRefreshFailed ? childrenError : null);
  // Една „Опитай пак“ за двете заявки: при паднали и двете — едно натискане, не две (ревю кр.3).
  const retryAll = () => {
    refetchAssignments();
    refetchChildren();
  };
  const { activeYear: schoolYear } = useActiveSchoolYear();
  const { holidays } = useHolidayAdmin(schoolYear || '');
  const childIds = useMemo(() => children.map((c) => c.childId), [children]);
  const { byChildId } = useChildrenCalendarSlots(childIds);

  /**
   * ⚠️ Отпечатък на ваканциите, не самият масив.
   *
   * `useHolidayAdmin` слуша с `onSnapshot` и връща НОВ масив при всяко събитие.
   * Подаден пряко като зависимост, той преизчислява при всяко трепване — точно
   * дефектът, който админският календар вече е платил и реши със стрингифициране.
   */
  const holidayKey = holidays
    .map((h) => `${h.holidayId}|${h.startDate?.toMillis?.() ?? ''}|${h.endDate?.toMillis?.() ?? ''}`)
    .join(';');

  const holidayViews = useMemo<TeacherHolidayView[]>(
    () =>
      holidays.map((h) => ({
        holidayId: h.holidayId,
        name: h.name,
        type: h.type,
        startDate: toIsoDate(h.startDate.toDate()),
        endDate: toIsoDate(h.endDate.toDate()),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [holidayKey],
  );

  const childColors = useMemo(
    () => childMarkColors(primaryColor, children.length),
    [primaryColor, children.length],
  );

  const parentCalendar = useMemo(
    () =>
      buildParentCalendarEvents(
        children.map((c, i) => ({
          childId: c.childId,
          childName: c.childName || c.childId,
          slots: byChildId[c.childId] ?? [],
          color: childColors[i] ?? childColors[0],
        })),
        holidayViews,
        {
          primaryColor,
          today: new Date(),
          schoolYear: schoolYear || undefined,
          kindLabels: {
            vacation: t('calendar.kind.vacation'),
            holiday: t('calendar.kind.holiday'),
            noSchool: t('calendar.kind.noSchool'),
          },
        },
      ),
    [children, byChildId, childColors, holidayViews, primaryColor, schoolYear, t],
  );

  // ── calendar events from fee installments ─────────────────────────────────
  const calendarEvents = useMemo(() => {
    const events: CalendarEvent[] = [];
    const now = new Date();
    const color = primaryColor || '#3b82f6';

    // Fee installment due dates (red)
    if (familyFee?.installments) {
      for (const inst of familyFee.installments) {
        if (!inst.dueDate) continue;
        const dateObj: Date | null = typeof inst.dueDate.toDate === 'function'
          ? inst.dueDate.toDate()
          : inst.dueDate instanceof Date ? inst.dueDate : null;
        if (!dateObj) continue;
        const dateStr = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${String(dateObj.getDate()).padStart(2, '0')}`;
        events.push({
          date: dateStr,
          label: t('dashboard.fees.installment', { number: inst.number }),
          color: '#ef4444',
        });
      }
    }

    void now;
    void color;

    // Часовете и ваканциите идват от `buildParentCalendarEvents`; вноските
    // остават както са били — днешното поведение не се губи (изискване 5).
    return [...events, ...parentCalendar.events];
  }, [familyFee, primaryColor, t, parentCalendar]);

  // ── derived stats ───────────────────────────────────────────────────────────
  const stats = useMemo(() => ({
    total:   assignments.length,
    pending: assignments.filter(a => a.status === 'pending').length,
    signed:  assignments.filter(a => a.status === 'signed').length,
    declined: assignments.filter(a => a.status === 'declined').length,
  }), [assignments]);

  // ── fee donut rings ─────────────────────────────────────────────────────────
  const feeRings = useMemo(() => {
    if (!familyFee) return null;
    const installments = familyFee.installments || [];
    const total = installments.length;
    if (total === 0) return null;

    // Баланс ≤ 0 → няма неплатена вноска (TASK_54 В7). Запис без `summary` (рендерът по-долу го
    // допуска) → NaN: без гарда, състоянието се вади само от вноската — не се приема за платено.
    const balance = familyFee.summary?.balance ?? NaN;
    const paid    = installments.filter(i => installmentStatus(i, balance) === 'paid').length;
    const dueOver = installments.filter(i => installmentStatus(i, balance) === 'due' || installmentStatus(i, balance) === 'overdue').length;
    const upcoming = installments.filter(i => installmentStatus(i, balance) === 'upcoming').length;

    const p = donutPalette[1]; // fees use palette slot 1 (same as admin)
    return [
      { name: t('dashboard.fees.paid'),     value: paid,     total, color: p.bold },
      { name: t('dashboard.fees.due'),      value: dueOver,  total, color: p.muted },
      { name: t('dashboard.fees.upcoming'), value: upcoming, total, color: p.third },
    ];
  }, [familyFee, donutPalette, t]);

  const recentAssignments = assignments.slice(0, 5);

  const BG_TRACK = '#e8ecf0';
  const ringRadii = [
    { outer: 62, inner: 48 },
    { outer: 42, inner: 28 },
    { outer: 22, inner: 10 },
  ];

  // ── render ──────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <LoadingSpinner fullScreen />
    );
  }

  // Профил без семейство: декларациите и таксите НЕ са потърсени — не „0", а обяснение.
  if (notLinked) {
    return (
      <div className="space-y-6">
        <PageHeader
          title={t('dashboard.welcome', { name: userProfile?.displayName || 'Parent' })}
          subtitle={t('dashboard.subtitle')}
        />
        <StatusPanel variant="warning" title={t('dashboard.notLinked.title')}>
          {t('dashboard.notLinked.body')}
        </StatusPanel>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 1. Page header */}
      <PageHeader
        title={t('dashboard.welcome', { name: userProfile?.displayName || 'Parent' })}
        subtitle={t('dashboard.subtitle')}
      />

      {/* Декларациите не са заредени → казано тук; таксите и календарът остават (19.10, № 1116). */}
      {assignmentsLoadError ? (
        <AssignmentsLoadProblem error={assignmentsLoadError} onRetry={retryAll} />
      ) : childrenLoadError ? (
        <LoadProblem
          error={childrenLoadError}
          onRetry={retryAll}
          failedTitle={t('children.loadError')}
          offlineBody={t('children.state.offlineBody')}
        />
      ) : refreshError ? (
        <RefreshProblem error={refreshError} onRetry={retryAll} />
      ) : null}

      {/* 2. Stat cards — числа САМО върху успешно заредени данни (TASK_44 §1.2) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={<FileText size={22} />}    label={t('dashboard.stats.total')}   value={assignmentsLoaded ? stats.total : '—'}    color="blue" />
        <StatCard icon={<Clock size={22} />}        label={t('dashboard.stats.pending')} value={assignmentsLoaded ? stats.pending : '—'}  color="amber" />
        <StatCard icon={<CheckCircle size={22} />}  label={t('dashboard.stats.signed')}  value={assignmentsLoaded ? stats.signed : '—'}   color="green" />
        <StatCard icon={<XCircle size={22} />}      label={t('dashboard.stats.declined')} value={assignmentsLoaded ? stats.declined : '—'} color="red" />
      </div>

      {/* 3. Fee donut + Calendar */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Fee donut — 2/3 */}
        <div className="lg:col-span-2">
          {feeRings ? (
            <Link
              to="/fees"
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 hover:shadow-lg hover:border-primary-300 transition-all cursor-pointer group block"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-800">{t('dashboard.fees.title')}</h3>
                <span className="text-gray-400 text-lg leading-none tracking-widest">···</span>
              </div>

              <div className="flex items-center gap-6">
                {/* Concentric rings */}
                <div className="flex-shrink-0">
                  <PieChart width={140} height={140}>
                    {feeRings.map((ring, i) => {
                      const pct = ring.total > 0 ? Math.round((ring.value / ring.total) * 100) : 0;
                      const r = ringRadii[i] || ringRadii[ringRadii.length - 1];
                      const ringData = ring.total > 0
                        ? [{ value: pct, fill: ring.color }, { value: 100 - pct, fill: BG_TRACK }]
                        : [{ value: 100, fill: BG_TRACK }];
                      return (
                        <Pie
                          key={ring.name}
                          data={ringData}
                          cx={69} cy={69}
                          innerRadius={r.inner} outerRadius={r.outer}
                          startAngle={90} endAngle={-270}
                          paddingAngle={0}
                          dataKey="value"
                          stroke="none"
                          isAnimationActive={false}
                        >
                          {ringData.map((entry, j) => (
                            <Cell key={j} fill={entry.fill} />
                          ))}
                        </Pie>
                      );
                    })}
                  </PieChart>
                </div>

                {/* Legend */}
                <div className="flex flex-col justify-center gap-3 min-w-0">
                  {feeRings.map((ring) => {
                    const pct = ring.total > 0 ? Math.round((ring.value / ring.total) * 100) : 0;
                    return (
                      <div key={ring.name}>
                        <div className="flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: ring.color }} />
                          <span className="text-xs text-gray-500">{ring.name}</span>
                        </div>
                        <p className="text-lg font-bold text-gray-900 ml-[18px]">{pct}%</p>
                      </div>
                    );
                  })}
                  {familyFee?.summary && (
                    <p className="text-xs text-gray-400 mt-1 ml-[18px]">
                      {t('dashboard.fees.paidOf', { paid: familyFee.summary.totalPaid, total: familyFee.summary.totalDue })}
                    </p>
                  )}
                </div>
              </div>

              <p className="text-xs text-primary-600 group-hover:underline mt-4 pt-3 border-t border-gray-100">
                {t('dashboard.fees.viewAll')}
              </p>
            </Link>
          ) : (
            <ChartCard title={t('dashboard.fees.title')}>
              {/* „Няма данни" само върху УСПЕШНО заредена заявка; грешката се казва, не се гълта. */}
              <div className="flex items-center justify-center h-36 text-gray-400 text-sm">
                {feesError ? (
                  <span role="alert" className="text-red-700">
                    {t('dashboard.fees.loadError', { error: feesError.message })}
                  </span>
                ) : feesLoaded ? (
                  t('dashboard.fees.noData')
                ) : (
                  '—'
                )}
              </div>
            </ChartCard>
          )}
        </div>

        {/* Calendar — 1/3 */}
        <div>
          <CalendarWidget events={calendarEvents} />
          {/*
            🔴 Легендата назовава ВСЯКО дете (изискване 6). При маркер от 4 пиксела
            цветът сам не носи чие е — окото вижда „днес има нещо", а кой точно се
            чете тук и в панела на избрания ден.

            ⚠️ `lesson` отпада, когато има деца: цветът на първото дете Е цветът на
            часа, тоест два записа за едно и също нещо. `assistant` не се рисува на
            родителския екран изобщо.
          */}
          <CalendarLegend
            primaryColor={primaryColor}
            omit={parentCalendar.legend.length > 0 ? ['assistant', 'lesson'] : ['assistant']}
            extra={parentCalendar.legend.map((l) => ({
              label: l.hasSchedule ? l.childName : `${l.childName} (${t('dashboard.calendar.noSchedule')})`,
              color: l.color,
              muted: !l.hasSchedule,
            }))}
            className="mt-3"
          />
        </div>
      </div>

      {/* 4. Quick links */}
      <ChartCard title={t('dashboard.quickLinks.title')}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { to: '/assignments', icon: <ClipboardList size={22} />, label: t('dashboard.quickLinks.declarations'), desc: t('dashboard.quickLinks.declarationsDesc') },
            { to: '/children',   icon: <Users size={22} />,         label: t('dashboard.quickLinks.children'),      desc: t('dashboard.quickLinks.childrenDesc') },
            { to: '/fees',       icon: <Wallet size={22} />,        label: t('dashboard.quickLinks.fees'),           desc: t('dashboard.quickLinks.feesDesc') },
            { to: '/volunteer',  icon: <Heart size={22} />,         label: t('dashboard.quickLinks.volunteer'),      desc: t('dashboard.quickLinks.volunteerDesc') },
          ].map(({ to, icon, label, desc }) => (
            <Link
              key={to}
              to={to}
              className="flex items-center gap-3 p-4 rounded-lg border-2 border-gray-200 hover:border-primary-400 hover:bg-primary-50 transition-colors"
            >
              <span className="text-primary-600">{icon}</span>
              <div>
                <p className="font-medium text-gray-900 text-sm">{label}</p>
                <p className="text-xs text-gray-500">{desc}</p>
              </div>
            </Link>
          ))}
        </div>
      </ChartCard>

      {/* 5. Recent declarations */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">{t('dashboard.recent.title')}</h2>
          <Link to="/assignments" className="text-sm font-medium text-primary-600 hover:text-primary-700">
            {t('dashboard.recent.viewAll')}
          </Link>
        </div>

        {!assignmentsLoaded ? (
          <div className="text-center py-10 text-gray-400">—</div>
        ) : recentAssignments.length === 0 ? (
          <div className="text-center py-10 text-gray-400">{t('dashboard.recent.noDeclarations')}</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {recentAssignments.map((assignment) => {
              const dueDate   = assignment.dueDate?.toDate?.();
              const dueDateStr = dueDate ? dueDate.toLocaleDateString(dateLocaleFor(lang)) : '';
              const signedAt  = assignment.metadata?.lastStatusChangeAt?.toDate?.();
              const signedAtStr = signedAt ? signedAt.toLocaleDateString(dateLocaleFor(lang)) : '';

              return (
                <div
                  key={assignment.assignmentId}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-4 gap-3"
                >
                  <div>
                    <p className="font-medium text-gray-900">
                      {liveDeclarationTitle(declarationByKey.get(assignment.declarationCode), lang) ||
                        assignment.declarationCode}
                    </p>
                    <p className="text-sm text-gray-500 mt-0.5">{assignment.childName}</p>
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    {assignment.status === 'pending' ? (
                      <>
                        {dueDateStr && (
                          <span className="text-xs text-amber-600">{t('dashboard.recent.deadline', { date: dueDateStr })}</span>
                        )}
                        <Badge variant="warning" size="md">{t('dashboard.recent.awaitingSignature')}</Badge>
                        <Link
                          to={`/assignments?assignmentId=${encodeURIComponent(assignment.assignmentId)}`}
                          className="btn btn-primary text-sm"
                        >
                          {t('dashboard.recent.sign')}
                        </Link>
                      </>
                    ) : assignment.status === 'signed' ? (
                      <>
                        {signedAtStr && (
                          <span className="text-xs text-gray-500">{t('dashboard.recent.signedAt', { date: signedAtStr })}</span>
                        )}
                        <Badge variant="success" size="md">{t('dashboard.recent.signed')}</Badge>
                      </>
                    ) : (
                      <Badge variant="danger" size="md">{t('dashboard.recent.declined')}</Badge>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
